import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, Select, DatePicker, Button, Table, message, Space, Spin, Row, Col, 
  Typography, Layout, Tag, Cascader, Modal, Tooltip, Radio
} from 'antd';
import { DownloadOutlined, MenuFoldOutlined, MenuUnfoldOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import Sidebar from './Sidebar';
import MainHeader from './MainHeader';
import moment from 'moment';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Content } = Layout;

const ATT_STAT_TONES = {
  present: { color: '#389e0d', bg: '#f6ffed', border: '#b7eb8f' },
  half: { color: '#d46b08', bg: '#fff7e6', border: '#ffd591' },
  absent: { color: '#cf1322', bg: '#fff1f0', border: '#ffa39e' },
  ot: { color: '#0958d9', bg: '#e6f4ff', border: '#91caff' },
  late: { color: '#c41d7f', bg: '#fff0f6', border: '#ffadd2' },
  leave: { color: '#1677ff', bg: '#e6f4ff', border: '#91caff' },
};

function AttMatrixStat({ value, tone, onClick, title }) {
  const t = ATT_STAT_TONES[tone] || ATT_STAT_TONES.present;
  const n = Number(value) || 0;
  const pill = (
    <span
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 26,
        height: 22,
        padding: '0 6px',
        fontSize: 11,
        fontWeight: 700,
        color: t.color,
        background: t.bg,
        border: `1px solid ${t.border}`,
        borderRadius: 6,
        lineHeight: 1,
      }}
    >
      {n}
    </span>
  );
  if (onClick && n > 0) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer' }}
      >
        {pill}
      </button>
    );
  }
  return pill;
}

function renderAttendanceDayCell(text) {
  if (!text || text === '-') {
    return <span style={{ color: '#bfbfbf', fontSize: 11 }}>—</span>;
  }
  const base = { fontSize: 11, lineHeight: 1.25 };
  const code = String(text).split(/[\s(]/)[0];
  const colorMap = {
    A: '#ff4d4f',
    P: '#52c41a',
    WO: '#722ed1',
    H: '#fa8c16',
    HD: '#fa8c16',
    L: '#1677ff',
  };
  if (colorMap[code] && text === code) {
    return <span style={{ ...base, color: colorMap[code], fontWeight: 700 }}>{code}</span>;
  }
  if (String(text).length <= 10) {
    return <span style={{ ...base, color: '#595959' }}>{text}</span>;
  }
  return (
    <Tooltip title={text}>
      <span style={{ ...base, color: '#595959', maxWidth: 72, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle' }}>
        {text}
      </span>
    </Tooltip>
  );
}

const OrgReports = () => {
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState(['attendance']);
  const [month, setMonth] = useState(moment().format('YYYY-MM'));
  const [data, setData] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyTitle, setHistoryTitle] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [reportScope, setReportScope] = useState('all'); // 'all' or 'selected'
  const [absentModalOpen, setAbsentModalOpen] = useState(false);
  const [selectedStaffAbsent, setSelectedStaffAbsent] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [salaryRegisterEnabled, setSalaryRegisterEnabled] = useState(true);
  const [monthlySummaryEnabled, setMonthlySummaryEnabled] = useState(true);
  const [perDaySalaryEnabled, setPerDaySalaryEnabled] = useState(true);
  const [comparisonEnabled, setComparisonEnabled] = useState(true);
  const [otImpactEnabled, setOtImpactEnabled] = useState(true);
  const [latePenaltyEnabled, setLatePenaltyEnabled] = useState(true);
  const [attendanceDateMode, setAttendanceDateMode] = useState('range'); // 'range' | 'month'
  const [attendanceRange, setAttendanceRange] = useState([
    dayjs().startOf('month'),
    dayjs().startOf('month').date(10)
  ]);
  const activeRequestRef = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();

  const applyRangePreset = (presetKey) => {
    const base = (attendanceRange && attendanceRange[0]) ? dayjs(attendanceRange[0]) : dayjs(month);
    const startOfMonth = base.startOf('month');
    const endOfMonth = base.endOf('month');
    if (presetKey === '1-10') {
      setAttendanceRange([startOfMonth, startOfMonth.date(10)]);
    } else if (presetKey === '11-20') {
      setAttendanceRange([startOfMonth.date(11), startOfMonth.date(20)]);
    } else if (presetKey === '21-end') {
      setAttendanceRange([startOfMonth.date(21), endOfMonth]);
    } else if (presetKey === 'full') {
      setAttendanceRange([startOfMonth, endOfMonth]);
    }
  };

  const isRangePreset = (presetKey) => {
    if (!attendanceRange || !attendanceRange[0] || !attendanceRange[1]) return false;
    const s = dayjs(attendanceRange[0]).format('YYYY-MM-DD');
    const e = dayjs(attendanceRange[1]).format('YYYY-MM-DD');
    const base = dayjs(attendanceRange[0]);
    const som = base.startOf('month');
    const eom = base.endOf('month');
    if (presetKey === '1-10') {
      return s === som.format('YYYY-MM-DD') && e === som.date(10).format('YYYY-MM-DD');
    }
    if (presetKey === '11-20') {
      return s === som.date(11).format('YYYY-MM-DD') && e === som.date(20).format('YYYY-MM-DD');
    }
    if (presetKey === '21-end') {
      return s === som.date(21).format('YYYY-MM-DD') && e === eom.format('YYYY-MM-DD');
    }
    if (presetKey === 'full') {
      return s === som.format('YYYY-MM-DD') && e === eom.format('YYYY-MM-DD');
    }
    return false;
  };

  // Fetch employees and departments for selection
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await api.get('/admin/staff');
        if (response.data.success) {
          const arr = Array.isArray(response.data.staff)
            ? response.data.staff
            : (Array.isArray(response.data.data) ? response.data.data : []);
          setEmployees(arr);
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };
    const fetchDepartments = async () => {
      try {
        const response = await api.get('/admin/business-functions');
        if (response.data.success) {
          const list = response.data.data || [];
          const deptFn = list.find((f) => String(f.name || '').toLowerCase() === 'department');
          const values = Array.isArray(deptFn?.values) ? deptFn.values : [];
          const items = values
            .filter((v) => v && v.value)
            .map((v) => ({ id: v.id, name: v.value }));
          setDepartments(items);
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };
    fetchEmployees();
    fetchDepartments();
  }, []);

  // Fetch subscription info to check feature toggles
  useEffect(() => {
    const fetchSubInfo = async () => {
      try {
        const response = await api.get('/subscription/subscription-info');
        if (response.data.success && response.data.subscriptionInfo) {
          const subInfo = response.data.subscriptionInfo;
          setSalaryRegisterEnabled(subInfo.salaryRegisterEnabled !== undefined ? !!subInfo.salaryRegisterEnabled : true);
          setMonthlySummaryEnabled(subInfo.monthlySummaryEnabled !== undefined ? !!subInfo.monthlySummaryEnabled : true);
          setPerDaySalaryEnabled(subInfo.perDaySalaryEnabled !== undefined ? !!subInfo.perDaySalaryEnabled : true);
          setComparisonEnabled(subInfo.comparisonEnabled !== undefined ? !!subInfo.comparisonEnabled : true);
          setOtImpactEnabled(subInfo.otImpactEnabled !== undefined ? !!subInfo.otImpactEnabled : true);
          setLatePenaltyEnabled(subInfo.latePenaltyEnabled !== undefined ? !!subInfo.latePenaltyEnabled : true);
        }
      } catch (error) {
        console.error('Error fetching subscription info:', error);
      }
    };
    fetchSubInfo();
  }, []);

  const reportOptions = React.useMemo(() => {
    const options = [
      { value: 'attendance', label: 'Attendance Report' },
      { value: 'absent-report', label: 'Absent Report' },
      { value: 'monthly-attendance', label: 'Monthly Attendance (Detailed Excel)' },
      { value: 'leave', label: 'Leave Report' },
      { value: 'applied-leave', label: 'Applied Leave Report' },
      { value: 'leave-balance', label: 'Leave Balance Report' },
      { value: 'punch-report', label: 'Punch Report (Matrix)' },
      { value: 'sales', label: 'Visit Report' },
      { value: 'activities', label: 'Activities Report' },
      { value: 'tickets', label: 'Tickets Report' },
      { value: 'meetings', label: 'Meetings Report' },
      { value: 'staff-login-logout', label: 'Staff Login/Logout Report' },
      { value: 'labour-wages', label: 'Labour / Worker Wages Report (Mon-Sat)' },
    ];

    if (salaryRegisterEnabled) {
      options.push({
        value: 'salary-register',
        label: 'Salary Register (Excel)',
        children: [
          { value: 'designation', label: 'Designation Wise' },
          { value: 'department', label: 'Department Wise' },
          { value: 'template', label: 'Salary Template Wise' }
        ]
      });
    }

    if (monthlySummaryEnabled) {
      options.push({
        value: 'monthly-summary',
        label: 'Monthly Summary (Excel)',
        children: [
          { value: 'designation', label: 'Designation Wise' },
          { value: 'department', label: 'Department Wise' }
        ]
      });
    }

    if (perDaySalaryEnabled) {
      options.push({ value: 'per-day-salary', label: 'Per Day Salary Average (with OT)' });
    }

    if (comparisonEnabled) {
      options.push({ value: 'comparison', label: 'Month-over-Month Comparison' });
    }

    if (otImpactEnabled) {
      options.push({ value: 'ot-impact', label: 'Overtime Impact Analysis' });
    }

    if (latePenaltyEnabled) {
      options.push({ value: 'late-penalty', label: 'Late Penalty Analysis' });
    }

    return options;
  }, [salaryRegisterEnabled, monthlySummaryEnabled, perDaySalaryEnabled, comparisonEnabled, otImpactEnabled, latePenaltyEnabled]);

  // Handle report type from query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const type = params.get('type');
    if (type && reportOptions.some(rt => rt.value === type)) {
      setReportType([type]);
    }
  }, [location.search, reportOptions]);

  const months = [];
  for (let i = 0; i < 12; i++) {
    const date = moment().subtract(i, 'months');
    months.push({
      value: date.format('YYYY-MM'),
      label: date.format('MMMM YYYY')
    });
  }

  useEffect(() => {
    if (reportType) {
      fetchReportData();
    }
  }, [reportType, month, reportScope, selectedEmployees, selectedDepartment, attendanceDateMode, attendanceRange]);

  useEffect(() => {
    const mainType = reportType[0];
    if (mainType !== 'attendance' && mainType !== 'monthly-attendance') {
      setSelectedDepartment('');
    }
  }, [reportType]);

  const fetchReportData = async () => {
    const requestId = ++activeRequestRef.current;

    const mainType = reportType[0];
    if (mainType === 'monthly-attendance' || mainType === 'salary-register' || mainType === 'monthly-summary') {
      // These reports are Excel-only; clear preview data
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      let endpoint;
      if (mainType === 'attendance') {
        endpoint = '/admin/reports/org-attendance-matrix';
      } else if (mainType === 'absent-report') {
        endpoint = '/admin/reports/absent-report';
      } else if (mainType === 'monthly-attendance') {
        endpoint = '/admin/reports/monthly-attendance';
      } else if (mainType === 'leave') {
        endpoint = '/admin/reports/org-leave';
      } else if (mainType === 'applied-leave') {
        endpoint = '/admin/reports/org-applied-leave';
      } else if (mainType === 'leave-balance') {
        endpoint = '/admin/reports/org-leave-balance';
      } else if (mainType === 'punch-report') {
        endpoint = '/admin/reports/org-punch-matrix';
      } else if (mainType === 'detailed-attendance') {
        endpoint = '/admin/reports/org-detailed-attendance';
      } else if (mainType === 'sales') {
        endpoint = '/admin/reports/org-sales';
      } else if (mainType === 'activities') {
        endpoint = '/admin/reports/org-activities';
      } else if (mainType === 'tickets') {
        endpoint = '/admin/reports/org-tickets';
      } else if (mainType === 'meetings') {
        endpoint = '/admin/reports/org-meetings';
      } else if (mainType === 'per-day-salary') {
        endpoint = '/admin/reports/per-day-salary-average';
      } else if (mainType === 'comparison') {
        endpoint = '/admin/reports/comparison';
      } else if (mainType === 'ot-impact') {
        endpoint = '/admin/reports/ot-impact';
      } else if (mainType === 'late-penalty') {
        endpoint = '/admin/reports/late-penalty-analysis';
      } else if (mainType === 'staff-login-logout') {
        endpoint = '/admin/reports/staff-login-logout';
      }

      const params = {};
      if (mainType === 'staff-login-logout') {
        params.fromDate = moment(month).startOf('month').format('YYYY-MM-DD');
        params.toDate = moment(month).endOf('month').format('YYYY-MM-DD');
        if (reportScope === 'selected' && selectedEmployees.length > 0) {
          params.staffId = selectedEmployees[0];
        }
      } else if (mainType === 'attendance') {
        if (attendanceDateMode === 'range' && attendanceRange && attendanceRange[0] && attendanceRange[1]) {
          const sObj = dayjs(attendanceRange[0]);
          const eObj = dayjs(attendanceRange[1]);
          params.startDate = sObj.format('YYYY-MM-DD');
          params.endDate = eObj.format('YYYY-MM-DD');
          params.month = sObj.month() + 1;
          params.year = sObj.year();
        } else {
          params.month = moment(month).month() + 1;
          params.year = moment(month).year();
          params.startDate = moment(month).startOf('month').format('YYYY-MM-DD');
          params.endDate = moment(month).endOf('month').format('YYYY-MM-DD');
        }
        if (reportScope === 'selected' && selectedEmployees.length > 0) {
          params.employeeIds = selectedEmployees.join(',');
        }
      } else {
        params.month = moment(month).month() + 1;
        params.year = moment(month).year();
        if (reportScope === 'selected' && selectedEmployees.length > 0) {
          params.employeeIds = selectedEmployees.join(',');
        }
      }
      if (selectedDepartment) {
        params.department = selectedDepartment;
      }

      const response = await api.get(endpoint, { params });

      if (requestId !== activeRequestRef.current) return;

      if (response.data.success) {
        setReportData(response.data.data);
        if (mainType === 'punch-report') {
          const { staffList, matrix, daysInMonth, startDate } = response.data.data;
          const formatted = staffList.map((staff, idx) => {
            const row = { id: staff.id, sn: idx + 1, staffName: staff.profile?.name || 'N/A' };
            for (let i = 1; i <= daysInMonth; i++) {
              const d = new Date(startDate);
              d.setDate(i);
              const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
              row[`day_${i}`] = (matrix[staff.id] && matrix[staff.id][dateKey]) ? matrix[staff.id][dateKey].join(', ') : '-';
            }
            return row;
          });
          setData(formatted);
        } else if (mainType === 'attendance') {
          const { staffList, matrix, daysInMonth, startDate, summary, daysList } = response.data.data;
          const formatted = (staffList || []).map((staff, idx) => {
            const row = {
              id: staff.id,
              sn: idx + 1,
              staffName: staff.profile?.name || 'N/A',
              presentDays: summary?.[staff.id]?.presentDays || 0,
              halfDays: summary?.[staff.id]?.halfDays || 0,
              leaveDays: summary?.[staff.id]?.leaveDays || 0,
              absentDays: summary?.[staff.id]?.absentDays || 0,
              overtimeMinutes: summary?.[staff.id]?.overtimeMinutes || 0,
              lateMinutes: summary?.[staff.id]?.lateMinutes || 0
            };
            if (Array.isArray(daysList) && daysList.length > 0) {
              daysList.forEach((dItem, dIdx) => {
                const val = (matrix && matrix[staff.id] && matrix[staff.id][dItem.date]) ? matrix[staff.id][dItem.date] : '-';
                row[`day_${dIdx + 1}`] = val;
                row[`date_${dItem.date}`] = val;
              });
            } else {
              for (let i = 1; i <= (daysInMonth || 30); i++) {
                const d = new Date(startDate);
                d.setDate(i);
                const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                row[`day_${i}`] = (matrix && matrix[staff.id] && matrix[staff.id][dateKey]) ? matrix[staff.id][dateKey] : '-';
              }
            }
            return row;
          });
          setData(formatted);
        } else if (mainType === 'per-day-salary') {
          const formatted = response.data.data.map((item, idx) => ({
            ...item,
            sn: idx + 1
          }));
          setData(formatted);
        } else if (mainType === 'comparison' || mainType === 'ot-impact' || mainType === 'late-penalty') {
          const formatted = response.data.data.map((item, idx) => ({
            ...item,
            sn: idx + 1
          }));
          setData(formatted);
        } else if (mainType === 'staff-login-logout') {
          const formatted = response.data.data.map((item, idx) => ({
            ...item,
            sn: idx + 1,
            key: item.userId
          }));
          setData(formatted);
        } else {
          setData(response.data.data);
        }
      } else {
        if (mainType !== 'monthly-attendance') {
          message.error('Failed to fetch report data');
        }
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      if (mainType !== 'monthly-attendance') {
        message.error('Error loading report data');
      }
    } finally {
      if (requestId === activeRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const downloadExcel = async () => {
    const mainType = reportType[0];
    if (mainType === 'staff-login-logout') {
      setDownloading(true);
      try {
        let csvContent = "\uFEFF"; // UTF-8 BOM
        csvContent += "Staff Name,Phone,Event Type,Timestamp,Platform,IP Address,Latitude,Longitude,Address\r\n";
        
        data.forEach(staff => {
          (staff.events || []).forEach(evt => {
            const timeStr = moment(evt.timestamp).format('YYYY-MM-DD HH:mm:ss');
            const platformStr = evt.platform || 'N/A';
            const addressEscaped = evt.address ? `"${evt.address.replace(/"/g, '""')}"` : 'N/A';
            csvContent += `"${staff.staffName}","${staff.phone}","${evt.type}","${timeStr}","${platformStr}","${evt.ipAddress || 'N/A'}",${evt.latitude || 'N/A'},${evt.longitude || 'N/A'},${addressEscaped}\r\n`;
          });
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `staff_login_logout_report_${month}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        message.success('Report downloaded successfully');
      } catch (error) {
        console.error('Error downloading CSV:', error);
        message.error('Error downloading CSV');
      } finally {
        setDownloading(false);
      }
      return;
    }

    setDownloading(true);
    try {
      let endpoint;
      const subType = reportType[1];

      if (mainType === 'attendance') {
        endpoint = '/admin/reports/org-attendance-matrix';
      } else if (mainType === 'absent-report') {
        endpoint = '/admin/reports/absent-report';
      } else if (mainType === 'monthly-attendance') {
        endpoint = '/admin/reports/monthly-attendance';
      } else if (mainType === 'leave') {
        endpoint = '/admin/reports/org-leave';
      } else if (mainType === 'applied-leave') {
        endpoint = '/admin/reports/org-applied-leave';
      } else if (mainType === 'leave-balance') {
        endpoint = '/admin/reports/org-leave-balance';
      } else if (mainType === 'punch-report') {
        endpoint = '/admin/reports/org-punch-matrix';
      } else if (mainType === 'detailed-attendance') {
        endpoint = '/admin/reports/org-detailed-attendance';
      } else if (mainType === 'sales') {
        endpoint = '/admin/reports/org-sales';
      } else if (mainType === 'activities') {
        endpoint = '/admin/reports/org-activities';
      } else if (mainType === 'tickets') {
        endpoint = '/admin/reports/org-tickets';
      } else if (mainType === 'meetings') {
        endpoint = '/admin/reports/org-meetings';
      } else if (mainType === 'per-day-salary') {
        endpoint = '/admin/reports/per-day-salary-average';
      } else if (mainType === 'comparison') {
        endpoint = '/admin/reports/comparison';
      } else if (mainType === 'ot-impact') {
        endpoint = '/admin/reports/ot-impact';
      } else if (mainType === 'late-penalty') {
        endpoint = '/admin/reports/late-penalty-analysis';
      } else if (mainType === 'salary-register') {
        if (subType === 'template') {
          endpoint = '/admin/payroll/salary-register-template-wise-excel';
        } else {
          endpoint = '/admin/payroll/salary-register-excel-by-month';
        }
      } else if (mainType === 'salary-register-template') {
        endpoint = '/admin/payroll/salary-register-template-wise-excel';
      } else if (mainType === 'monthly-summary') {
        endpoint = '/admin/payroll/monthly-summary-excel';
      }

      const params = {
        month: moment(month).month() + 1,
        year: moment(month).year(),
        format: 'excel',
        monthKey: month,
        groupBy: subType || 'designation'
      };

      if (mainType === 'attendance') {
        if (attendanceDateMode === 'range' && attendanceRange && attendanceRange[0] && attendanceRange[1]) {
          const sObj = dayjs(attendanceRange[0]);
          const eObj = dayjs(attendanceRange[1]);
          params.startDate = sObj.format('YYYY-MM-DD');
          params.endDate = eObj.format('YYYY-MM-DD');
          params.month = sObj.month() + 1;
          params.year = sObj.year();
        } else {
          params.startDate = moment(month).startOf('month').format('YYYY-MM-DD');
          params.endDate = moment(month).endOf('month').format('YYYY-MM-DD');
        }
      }

      if (reportScope === 'selected' && selectedEmployees.length > 0) {
        params.employeeIds = selectedEmployees.join(',');
      }

      if (selectedDepartment) {
        params.department = selectedDepartment;
      }

      const response = await api.get(endpoint, {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const scopeText = reportScope === 'selected' ? 'selected-employees' : 'all-employees';
      let fileName = `org-${mainType}-report-${scopeText}-${month}.xlsx`;
      if (mainType === 'attendance') {
        if (attendanceDateMode === 'range' && attendanceRange?.[0] && attendanceRange?.[1]) {
          fileName = `attendance-report-${scopeText}-${dayjs(attendanceRange[0]).format('YYYY-MM-DD')}-to-${dayjs(attendanceRange[1]).format('YYYY-MM-DD')}.xlsx`;
        } else {
          fileName = `attendance-report-${scopeText}-${month}.xlsx`;
        }
      }
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success('Report downloaded successfully');
    } catch (error) {
      console.error('Error downloading report:', error);
      message.error('Error downloading report');
    } finally {
      setDownloading(false);
    }
  };

  const showHistory = (staffId, staffName, type) => {
    if (!reportData?.details?.[staffId]) return;
    const staffDetails = reportData.details[staffId];
    const items = Object.entries(staffDetails)
      .filter(([_, dayData]) => (type === 'late' ? dayData.late > 0 : dayData.ot > 0))
      .map(([date, dayData]) => ({
        key: date,
        date: date,
        status: dayData.status,
        minutes: type === 'late' ? dayData.late : dayData.ot
      }))
      .sort((a, b) => b.date.localeCompare(a.date));

    setHistoryItems(items);
    setHistoryTitle(`${type === 'late' ? 'Late Arrival' : 'Overtime'} History - ${staffName}`);
    setHistoryModalVisible(true);
  };

  const getSalesColumns = () => [
    {
      title: 'Employee Name',
      dataIndex: 'employeeName',
      key: 'employeeName',
      render: (text, record) => <span style={{ fontWeight: '600', color: '#1677ff' }}>{record.user?.profile?.name || 'N/A'}</span>
    },
    {
      title: 'Phone Number',
      dataIndex: 'employeeId',
      key: 'employeeId',
      render: (text, record) => record.user?.phone || 'N/A'
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (text, record) => record.user?.profile?.department || 'N/A'
    },
    {
      title: 'Client Name',
      dataIndex: 'clientName',
      key: 'clientName',
      render: (v) => <span style={{ fontWeight: '500', color: '#262626' }}>{v}</span>
    },
    {
      title: 'Visit Date',
      dataIndex: 'visitDate',
      key: 'visitDate',
      render: (text) => moment(text).format('DD MMM YYYY')
    },
    {
      title: 'Visit Type',
      dataIndex: 'visitType',
      key: 'visitType'
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      ellipsis: true
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone'
    }
  ];

  const getAttendanceColumns = () => {
    const cols = [
      { title: 'S.N.', dataIndex: 'sn', key: 'sn', fixed: 'left', width: 55, align: 'center' },
      { 
        title: 'Staff Name', 
        dataIndex: 'staffName', 
        key: 'staffName', 
        fixed: 'left', 
        width: 150,
        render: (v) => <span style={{ fontWeight: '600', color: '#1677ff' }}>{v}</span>
      }
    ];

    if (reportData?.daysList && Array.isArray(reportData.daysList) && reportData.daysList.length > 0) {
      reportData.daysList.forEach((dItem, idx) => {
        const isSun = dItem.dayName === 'Sun';
        cols.push({
          title: (
            <div style={{ lineHeight: 1.15, textAlign: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 12 }}>{String(dItem.dayNum).padStart(2, '0')}</div>
              <div style={{ fontSize: 10, color: isSun ? '#ff4d4f' : '#8c8c8c' }}>{dItem.dayName}</div>
            </div>
          ),
          dataIndex: `day_${idx + 1}`,
          key: `day_${idx + 1}`,
          width: 58,
          align: 'center',
          onHeaderCell: () => ({ style: { background: isSun ? '#fff1f0' : undefined, padding: '4px 2px' } }),
          onCell: () => ({ style: { padding: '4px 2px', background: isSun ? '#fffbfb' : undefined } }),
          render: (text) => renderAttendanceDayCell(text),
        });
      });
    } else {
      const days = moment(month, 'YYYY-MM').daysInMonth();
      for (let i = 1; i <= days; i++) {
        cols.push({
          title: i.toString().padStart(2, '0'),
          dataIndex: `day_${i}`,
          key: `day_${i}`,
          width: 58,
          align: 'center',
          onCell: () => ({ style: { padding: '4px 2px' } }),
          render: (text) => renderAttendanceDayCell(text),
        });
      }
    }

    cols.push({
      title: attendanceDateMode === 'range' ? 'Period totals' : 'Month totals',
      fixed: 'right',
      align: 'center',
      onHeaderCell: () => ({
        style: { background: '#fafafa', fontWeight: 600, fontSize: 11, textAlign: 'center' },
      }),
      children: [
        {
          title: <Tooltip title="Present days"><span style={{ color: '#389e0d' }}>P</span></Tooltip>,
          dataIndex: 'presentDays',
          key: 'presentDays',
          width: 38,
          align: 'center',
          onHeaderCell: () => ({ style: { padding: '4px 2px', fontSize: 11 } }),
          onCell: () => ({ style: { padding: '4px 2px', background: '#fcfcfc' } }),
          render: (v) => <AttMatrixStat value={v} tone="present" title="Present" />,
        },
        {
          title: <Tooltip title="Half days"><span style={{ color: '#d46b08' }}>HD</span></Tooltip>,
          dataIndex: 'halfDays',
          key: 'halfDays',
          width: 38,
          align: 'center',
          onHeaderCell: () => ({ style: { padding: '4px 2px', fontSize: 11 } }),
          onCell: () => ({ style: { padding: '4px 2px', background: '#fcfcfc' } }),
          render: (v) => <AttMatrixStat value={v} tone="half" title="Half day" />,
        },
        {
          title: <Tooltip title="Approved leave days"><span style={{ color: '#1677ff' }}>L</span></Tooltip>,
          dataIndex: 'leaveDays',
          key: 'leaveDays',
          width: 38,
          align: 'center',
          onHeaderCell: () => ({ style: { padding: '4px 2px', fontSize: 11 } }),
          onCell: () => ({ style: { padding: '4px 2px', background: '#fcfcfc' } }),
          render: (v) => <AttMatrixStat value={v} tone="leave" title="Leave" />,
        },
        {
          title: <Tooltip title="Absent days"><span style={{ color: '#cf1322' }}>A</span></Tooltip>,
          dataIndex: 'absentDays',
          key: 'absentDays',
          width: 38,
          align: 'center',
          onHeaderCell: () => ({ style: { padding: '4px 2px', fontSize: 11 } }),
          onCell: () => ({ style: { padding: '4px 2px', background: '#fcfcfc' } }),
          render: (v) => <AttMatrixStat value={v} tone="absent" title="Absent" />,
        },
        {
          title: <Tooltip title="Overtime (min)"><span style={{ color: '#0958d9' }}>OT</span></Tooltip>,
          dataIndex: 'overtimeMinutes',
          key: 'overtimeMinutes',
          width: 44,
          align: 'center',
          onHeaderCell: () => ({ style: { padding: '4px 2px', fontSize: 11 } }),
          onCell: () => ({ style: { padding: '4px 2px', background: '#fcfcfc' } }),
          render: (val, record) => (
            <AttMatrixStat
              value={val}
              tone="ot"
              title="OT minutes"
              onClick={val > 0 ? () => showHistory(record.id, record.staffName, 'ot') : undefined}
            />
          ),
        },
        {
          title: <Tooltip title="Late (min)"><span style={{ color: '#c41d7f' }}>Late</span></Tooltip>,
          dataIndex: 'lateMinutes',
          key: 'lateMinutes',
          width: 44,
          align: 'center',
          onHeaderCell: () => ({ style: { padding: '4px 2px', fontSize: 11 } }),
          onCell: () => ({ style: { padding: '4px 2px', background: '#fcfcfc' } }),
          render: (val, record) => (
            <AttMatrixStat
              value={val}
              tone="late"
              title="Late minutes"
              onClick={val > 0 ? () => showHistory(record.id, record.staffName, 'late') : undefined}
            />
          ),
        },
      ],
    });
    return cols;
  };

  const getLeaveColumns = () => [
    {
      title: 'Employee Name',
      dataIndex: 'employeeName',
      key: 'employeeName',
      render: (text, record) => <span style={{ fontWeight: '600', color: '#1677ff' }}>{record.user?.profile?.name || 'N/A'}</span>
    },
    {
      title: 'Phone Number',
      dataIndex: 'employeeId',
      key: 'employeeId',
      render: (text, record) => record.user?.phone || 'N/A'
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (text, record) => record.user?.profile?.department || 'N/A'
    },
    {
      title: 'Leave Type',
      dataIndex: 'leaveType',
      key: 'leaveType'
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (text) => moment(text).format('DD MMM YYYY')
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (text) => moment(text).format('DD MMM YYYY')
    },
    {
      title: 'Days',
      dataIndex: 'days',
      key: 'days',
      render: (text, record) => {
        const days = Math.ceil((new Date(record.endDate) - new Date(record.startDate)) / (1000 * 60 * 60 * 24)) + 1;
        return <span style={{ fontWeight: '600' }}>{days}</span>;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (v) => <span className="sales-status-tag sales-status-complete" style={{ textTransform: 'capitalize' }}>{v}</span>
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true
    }
  ];

  const getAppliedLeaveColumns = () => [
    {
      title: 'Employee Name',
      dataIndex: 'employeeName',
      key: 'employeeName',
      render: (text, record) => <span style={{ fontWeight: '600', color: '#1677ff' }}>{record.user?.profile?.name || 'N/A'}</span>
    },
    {
      title: 'Phone Number',
      key: 'phone',
      render: (text, record) => record.user?.phone || 'N/A'
    },
    {
      title: 'Department',
      key: 'department',
      render: (text, record) => record.user?.profile?.department || 'N/A'
    },
    {
      title: 'Leave Type',
      dataIndex: 'leaveType',
      key: 'leaveType'
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (text) => moment(text).format('DD MMM YYYY')
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (text) => moment(text).format('DD MMM YYYY')
    },
    {
      title: 'Days',
      dataIndex: 'days',
      key: 'days',
      render: (text, record) => {
        const days = Math.ceil((new Date(record.endDate) - new Date(record.startDate)) / (1000 * 60 * 60 * 24)) + 1;
        return <span style={{ fontWeight: '600' }}>{days}</span>;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text) => {
        const lower = String(text).toLowerCase();
        const cls = lower === 'approved' ? 'sales-status-complete' : lower === 'pending' ? 'sales-status-pending' : 'sales-status-inactive';
        return <span className={`sales-status-tag ${cls}`}>{text?.toUpperCase()}</span>;
      }
    },
    {
      title: 'Applied On',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => moment(text).format('DD MMM YYYY')
    }
  ];

  const getLeaveBalanceColumns = () => [
    {
      title: 'Employee Name',
      dataIndex: 'employeeName',
      key: 'employeeName',
      render: (text, record) => <span style={{ fontWeight: '600', color: '#1677ff' }}>{text || record.user?.profile?.name || 'N/A'}</span>
    },
    {
      title: 'Phone Number',
      dataIndex: 'employeeId',
      key: 'employeeId',
      render: (text, record) => text || record.user?.phone || 'N/A'
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (text, record) => text || record.user?.profile?.department || 'N/A'
    },
    {
      title: 'Category',
      dataIndex: 'categoryName',
      key: 'categoryName',
      render: (text, record) => text || record.category || record.categoryKey || 'N/A'
    },
    {
      title: 'Allocated',
      dataIndex: 'allocated',
      key: 'allocated'
    },
    {
      title: 'Used',
      dataIndex: 'used',
      key: 'used',
      render: (v) => <span style={{ color: v > 0 ? '#ff4d4f' : 'inherit', fontWeight: v > 0 ? '600' : 'normal' }}>{v}</span>
    },
    {
      title: 'Remaining',
      dataIndex: 'remaining',
      key: 'remaining',
      render: (v) => <span style={{ color: '#52c41a', fontWeight: '600' }}>{v}</span>
    }
  ];

  const getDetailedAttendanceColumns = () => [
    {
      title: 'Employee Name',
      dataIndex: 'employeeName',
      key: 'employeeName',
      render: (text, record) => <span style={{ fontWeight: '600', color: '#1677ff' }}>{record.user?.profile?.name || 'N/A'}</span>
    },
    {
      title: 'Phone Number',
      dataIndex: 'phone',
      key: 'phone',
      render: (text, record) => record.user?.phone || 'N/A'
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (text) => new Date(text).toLocaleDateString()
    },
    {
      title: 'Punch In',
      dataIndex: 'punchedInAt',
      key: 'punchedInAt',
      width: 100,
      render: (text) => text ? new Date(text).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A'
    },
    {
      title: 'Punch In Address',
      dataIndex: 'address',
      key: 'address',
      width: 250,
      render: (text, record) => text || (record.latitude ? `${record.latitude}, ${record.longitude}` : 'N/A')
    },
    {
      title: 'Punch Out',
      dataIndex: 'punchedOutAt',
      key: 'punchedOutAt',
      width: 100,
      render: (text) => text ? new Date(text).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A'
    },
    {
      title: 'Punch Out Address',
      dataIndex: 'punchOutAddress',
      key: 'punchOutAddress',
      width: 250,
      render: (text, record) => text || (record.punchOutLatitude ? `${record.punchOutLatitude}, ${record.punchOutLongitude}` : 'N/A')
    },
    {
      title: 'Assigned Geofence',
      dataIndex: 'assignedGeofence',
      key: 'assignedGeofence',
      width: 200,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (text) => {
        let cls = 'sales-status-pending';
        if (text === 'PRESENT') cls = 'sales-status-complete';
        else if (text === 'ABSENT') cls = 'sales-status-inactive';
        else if (text === 'HALF_DAY') cls = 'sales-status-pending';
        else if (text === 'LEAVE') cls = 'sales-status-active';
        return <span className={`sales-status-tag ${cls}`}>{text || 'N/A'}</span>;
      }
    }
  ];

  const getPunchColumns = () => {
    const days = moment(month, 'YYYY-MM').daysInMonth();
    const cols = [
      { title: 'S.N.', dataIndex: 'sn', key: 'sn', fixed: 'left', width: 60 },
      { 
        title: 'Staff Name', 
        dataIndex: 'staffName', 
        key: 'staffName', 
        fixed: 'left', 
        width: 150,
        render: (v) => <span style={{ fontWeight: '600', color: '#1677ff' }}>{v}</span>
      }
    ];
    for (let i = 1; i <= days; i++) {
      cols.push({
        title: i.toString().padStart(2, '0'),
        dataIndex: `day_${i}`,
        key: `day_${i}`,
        width: 100,
        align: 'center'
      });
    }
    return cols;
  };

  const getActivitiesColumns = () => [
    { title: 'Date', dataIndex: 'date', key: 'date', render: (text) => moment(text).format('DD MMM YYYY') },
    { title: 'Staff Name', key: 'staffName', render: (_, r) => <span style={{ fontWeight: '600', color: '#1677ff' }}>{r.user?.profile?.name || r.user?.phone || 'N/A'}</span> },
    { title: 'Department', key: 'department', render: (_, r) => r.user?.profile?.department || 'N/A' },
    { title: 'Activity Title', dataIndex: 'title', key: 'title', render: (v) => <span style={{ fontWeight: '500', color: '#262626' }}>{v}</span> },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (s) => {
        const cls = s === 'DONE' ? 'sales-status-complete' : s === 'IN_PROGRESS' ? 'sales-status-active' : 'sales-status-pending';
        return <span className={`sales-status-tag ${cls}`}>{s}</span>;
      }
    },
    { title: 'Last Remarks', dataIndex: 'remarks', key: 'remarks', ellipsis: true }
  ];

  const getTicketsColumns = () => [
    { title: 'Created At', dataIndex: 'createdAt', key: 'createdAt', render: (text) => moment(text).format('DD MMM YYYY HH:mm') },
    { title: 'Allocated To', key: 'allocatedTo', render: (_, r) => <span style={{ fontWeight: '600', color: '#1677ff' }}>{r.assignee?.profile?.name || r.assignee?.phone || 'N/A'}</span> },
    { title: 'Department', key: 'department', render: (_, r) => r.assignee?.profile?.department || 'N/A' },
    { title: 'Ticket Title', dataIndex: 'title', key: 'title', render: (v) => <span style={{ fontWeight: '500', color: '#262626' }}>{v}</span> },
    {
      title: 'Priority', dataIndex: 'priority', key: 'priority',
      render: (p) => {
        const cls = p === 'HIGH' ? 'sales-status-inactive' : p === 'MEDIUM' ? 'sales-status-pending' : 'sales-status-active';
        return <span className={`sales-status-tag ${cls}`}>{p}</span>;
      }
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (s) => {
        const cls = s === 'DONE' ? 'sales-status-complete' : s === 'IN_PROGRESS' ? 'sales-status-active' : 'sales-status-pending';
        return <span className={`sales-status-tag ${cls}`}>{s}</span>;
      }
    },
    { title: 'Allocated By', key: 'allocatedBy', render: (_, r) => r.creator?.profile?.name || r.creator?.phone || 'Admin' }
  ];

  const getMeetingsColumns = () => [
    { title: 'Scheduled At', dataIndex: 'scheduledAt', key: 'scheduledAt', render: (text) => moment(text).format('DD MMM YYYY HH:mm') },
    { title: 'Meeting Title', dataIndex: 'title', key: 'title', render: (v) => <span style={{ fontWeight: '500', color: '#262626' }}>{v}</span> },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (s) => {
        const cls = s === 'DONE' ? 'sales-status-complete' : s === 'IN_PROGRESS' ? 'sales-status-active' : 'sales-status-pending';
        return <span className={`sales-status-tag ${cls}`}>{s}</span>;
      }
    },
    { title: 'Created By', key: 'staffName', render: (_, r) => r.creator?.profile?.name || r.creator?.phone || 'N/A' },
    { title: 'Department', key: 'department', render: (_, r) => r.creator?.profile?.department || 'N/A' },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true }
  ];
  
  const getPerDaySalaryColumns = () => [
    { title: 'S.N.', dataIndex: 'sn', key: 'sn', width: 60, fixed: 'left' },
    { 
      title: 'Staff Name', 
      dataIndex: 'staffName', 
      key: 'staffName', 
      width: 150, 
      fixed: 'left',
      render: (v) => <span style={{ fontWeight: '600', color: '#1677ff' }}>{v}</span>
    },
    { title: 'Staff ID', dataIndex: 'staffId', key: 'staffId', width: 100 },
    { title: 'Department', dataIndex: 'department', key: 'department', width: 120 },
    { title: 'Designation', dataIndex: 'designation', key: 'designation', width: 120 },
    { title: 'Actual Earnings', dataIndex: 'actualEarnings', key: 'actualEarnings', width: 120, render: (v) => `₹${Number(v).toLocaleString()}` },
    { title: 'OT Pay', dataIndex: 'overtimePay', key: 'overtimePay', width: 100, render: (v) => `₹${Number(v).toLocaleString()}` },
    { title: 'Incentives', dataIndex: 'incentives', key: 'incentives', width: 100, render: (v) => `₹${Number(v).toLocaleString()}` },
    { title: 'Payable Days', dataIndex: 'payableDays', key: 'payableDays', width: 100 },
    { 
      title: 'Per Day Avg', 
      dataIndex: 'perDayAverage', 
      key: 'perDayAverage', 
      width: 120, 
      render: (v) => <span style={{ fontWeight: '700', color: '#1677ff' }}>₹{Number(v).toLocaleString()}</span>
    }
  ];

  const getComparisonColumns = () => [
    { 
      title: 'Staff Details', 
      fixed: 'left', 
      children: [
        { title: 'S.N.', dataIndex: 'sn', key: 'sn', width: 60 },
        { 
          title: 'Name', 
          dataIndex: 'staffName', 
          key: 'staffName', 
          width: 150,
          render: (v) => <span style={{ fontWeight: '600', color: '#1677ff' }}>{v}</span>
        },
      ]
    },
    { 
      title: 'Salary Comparison (Net)', 
      align: 'center', 
      children: [
        { title: 'Last Month', dataIndex: ['salary', 'lastMonth'], key: 'salLast', width: 120, render: (v) => `₹${v?.toLocaleString() || '0'}` },
        { title: 'Current Month', dataIndex: ['salary', 'currentMonth'], key: 'salCurr', width: 120, render: (v) => `₹${v?.toLocaleString() || '0'}` },
        { 
          title: 'Difference', dataIndex: ['salary', 'diff'], key: 'salDiff', width: 100, 
          render: (v) => <span style={{ fontWeight: '600', color: v > 0 ? '#52c41a' : v < 0 ? '#ff4d4f' : '#8c8c8c' }}>{v > 0 ? '+' : ''}{v?.toLocaleString() || '0'}</span>
        },
      ]
    },
    { 
      title: 'Attendance (Days)', 
      align: 'center', 
      children: [
        { title: 'Last Month', dataIndex: ['attendance', 'lastMonth'], key: 'attLast', width: 110 },
        { title: 'Current Month', dataIndex: ['attendance', 'currentMonth'], key: 'attCurr', width: 110 },
        { 
          title: 'Diff', dataIndex: ['attendance', 'diff'], key: 'attDiff', width: 80,
          render: (v) => <span style={{ fontWeight: '600', color: v > 0 ? '#52c41a' : v < 0 ? '#ff4d4f' : '#8c8c8c' }}>{v > 0 ? '+' : ''}{v}</span>
        },
      ]
    },
    { 
      title: 'Overtime Pay', 
      align: 'center', 
      children: [
        { title: 'Last Month', dataIndex: ['ot', 'lastMonth'], key: 'otLast', width: 110, render: (v) => `₹${v?.toLocaleString() || '0'}` },
        { title: 'Current Month', dataIndex: ['ot', 'currentMonth'], key: 'otCurr', width: 110, render: (v) => `₹${v?.toLocaleString() || '0'}` },
        { 
          title: 'Diff', dataIndex: ['ot', 'diff'], key: 'otDiff', width: 100,
          render: (v) => <span style={{ fontWeight: '600', color: v > 0 ? '#52c41a' : v < 0 ? '#ff4d4f' : '#8c8c8c' }}>{v > 0 ? '+' : ''}{v?.toLocaleString() || '0'}</span>
        },
      ]
    },
  ];

  const getLatePenaltyColumns = () => [
    { title: 'S.N.', dataIndex: 'sn', key: 'sn', width: 60, fixed: 'left' },
    { 
      title: 'Staff Name', 
      dataIndex: 'staffName', 
      key: 'staffName', 
      width: 150, 
      fixed: 'left',
      render: (v) => <span style={{ fontWeight: '600', color: '#1677ff' }}>{v}</span>
    },
    { title: 'Department', dataIndex: 'department', key: 'department', width: 120 },
    { title: 'Applied Rule', dataIndex: 'ruleName', key: 'ruleName', width: 150 },
    { title: 'Type', dataIndex: 'penaltyType', key: 'penaltyType', width: 120 },
    { title: 'Late Days', dataIndex: 'lateInstances', key: 'lateInstances', width: 100, align: 'center', render: (v) => <span style={{ fontWeight: '500' }}>{v}</span> },
    { title: 'Total Late Min', dataIndex: 'totalLateMinutes', key: 'totalLateMinutes', width: 120, align: 'center', render: (v) => <span style={{ fontWeight: '500' }}>{v} m</span> },
    { title: 'Total Penalty', dataIndex: 'totalPenaltyAmount', key: 'totalPenaltyAmount', width: 130, render: (v) => <span style={{ fontWeight: '700', color: '#ff4d4f' }}>₹{v?.toLocaleString() || '0'}</span> },
  ];

  const getOTImpactColumns = () => [
    { title: 'S.N.', dataIndex: 'sn', key: 'sn', width: 60, fixed: 'left' },
    { 
      title: 'Staff Name', 
      dataIndex: 'staffName', 
      key: 'staffName', 
      width: 150, 
      fixed: 'left',
      render: (v) => <span style={{ fontWeight: '600', color: '#1677ff' }}>{v}</span>
    },
    { title: 'Department', dataIndex: 'department', key: 'department', width: 120 },
    { title: 'Net (Without OT)', dataIndex: 'netWithoutOT', key: 'netWithoutOT', width: 140, render: (v) => `₹${v?.toLocaleString() || '0'}` },
    { title: 'OT Pay', dataIndex: 'otPay', key: 'otPay', width: 120, render: (v) => <span style={{ fontWeight: '600', color: '#1677ff' }}>₹{v?.toLocaleString() || '0'}</span> },
    { title: 'Total Net (With OT)', dataIndex: 'totalNet', key: 'totalNet', width: 150, render: (v) => <span style={{ fontWeight: '700', color: '#52c41a' }}>₹{v?.toLocaleString() || '0'}</span> },
    { title: 'OT % of Base', dataIndex: 'otPercentage', key: 'otPercentage', width: 120, render: (v) => <span style={{ fontWeight: '600', color: '#fa8c16' }}>{v}%</span> },
  ];

  const getStaffLoginLogoutColumns = () => [
    { title: 'S.N.', dataIndex: 'sn', key: 'sn', width: 60 },
    { title: 'Staff Name', dataIndex: 'staffName', key: 'staffName', render: (v) => <span style={{ fontWeight: '600', color: '#1677ff' }}>{v}</span> },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    { title: 'Total Logins', dataIndex: 'totalLogins', key: 'totalLogins', render: (v) => <Tag color="green" style={{ fontWeight: '600' }}>{v}</Tag> },
    { title: 'Total Logouts', dataIndex: 'totalLogouts', key: 'totalLogouts', render: (v) => <Tag color="orange" style={{ fontWeight: '600' }}>{v}</Tag> },
  ];

  const renderLoginLogoutDetail = (record) => {
    const columns = [
      {
        title: 'Event Type',
        dataIndex: 'type',
        key: 'type',
        render: (type) => (
          <Tag color={type === 'Login' ? 'green' : 'orange'} style={{ fontWeight: '500' }}>
            {type}
          </Tag>
        ),
      },
      {
        title: 'Time',
        dataIndex: 'timestamp',
        key: 'timestamp',
        render: (t) => moment(t).format('DD MMM YYYY, hh:mm A'),
      },
      {
        title: 'Platform',
        dataIndex: 'platform',
        key: 'platform',
        render: (p) => (
          <Tag color={p?.includes('apk') ? 'blue' : 'purple'}>
            {p === 'mobile-apk' ? 'Mobile App' : p === 'admin-apk' ? 'Admin App' : (p || 'Web')}
          </Tag>
        ),
      },
      {
        title: 'IP Address',
        dataIndex: 'ipAddress',
        key: 'ipAddress',
        render: (ip) => ip || 'N/A',
      },
      {
        title: 'Location (Lat, Lng)',
        key: 'coords',
        render: (_, item) => {
          if (item.latitude && item.longitude) {
            return (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${item.latitude},${item.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontWeight: '500' }}
              >
                {Number(item.latitude).toFixed(6)}, {Number(item.longitude).toFixed(6)}
              </a>
            );
          }
          return 'N/A';
        },
      },
      {
        title: 'Address',
        dataIndex: 'address',
        key: 'address',
        render: (address) => address || 'N/A',
      },

    ];

    return (
      <Table
        columns={columns}
        dataSource={record.events}
        pagination={false}
        rowKey="id"
        size="small"
        style={{ margin: '10px 0' }}
      />
    );
  };

  const getAbsentReportColumns = () => [
    { title: 'S.N.', dataIndex: 'sn', key: 'sn', width: 60 },
    { title: 'Employee Name', dataIndex: 'staffName', key: 'staffName', render: (v) => <span style={{ fontWeight: '600', color: '#1677ff' }}>{v}</span> },
    { title: 'Staff ID', dataIndex: 'staffId', key: 'staffId' },
    { title: 'Department', dataIndex: 'department', key: 'department', render: (v) => <Tag color="blue">{v || 'General'}</Tag> },
    { title: 'Salary Month', dataIndex: 'salaryMonth', key: 'salaryMonth' },
    // { title: 'Total Working Days', dataIndex: 'totalWorkingDays', key: 'totalWorkingDays', align: 'right', render: (v) => <span style={{ fontWeight: '600' }}>{v}</span> },
    // { title: 'Total Present Days', dataIndex: 'totalPresentDays', key: 'totalPresentDays', align: 'right', render: (v) => <Tag color="green" style={{ fontWeight: '600' }}>{v}</Tag> },
    {
      title: 'Total Absent Days',
      dataIndex: 'totalAbsentDays',
      key: 'totalAbsentDays',
      align: 'right',
      render: (v, record) => (
        <Tag
          color={v > 0 ? "red" : "default"}
          style={{ fontWeight: '700', cursor: v > 0 ? 'pointer' : 'default', padding: '2px 8px' }}
          onClick={() => {
            if (v > 0 && record.absentDates && record.absentDates.length > 0) {
              setSelectedStaffAbsent(record);
              setAbsentModalOpen(true);
            }
          }}
        >
          {v} {v > 0 ? <EyeOutlined style={{ marginLeft: 6 }} /> : null}
        </Tag>
      )
    },
  ];

  const renderTable = () => (
    <Table
      columns={
        reportType[0] === 'attendance' ? getAttendanceColumns() :
          reportType[0] === 'absent-report' ? getAbsentReportColumns() :
          reportType[0] === 'monthly-attendance' ? [] :
            reportType[0] === 'leave' ? getLeaveColumns() :
              reportType[0] === 'applied-leave' ? getAppliedLeaveColumns() :
                reportType[0] === 'leave-balance' ? getLeaveBalanceColumns() :
                  reportType[0] === 'punch-report' ? getPunchColumns() :
                    reportType[0] === 'detailed-attendance' ? getDetailedAttendanceColumns() :
                      reportType[0] === 'activities' ? getActivitiesColumns() :
                        reportType[0] === 'tickets' ? getTicketsColumns() :
                          reportType[0] === 'meetings' ? getMeetingsColumns() :
                            reportType[0] === 'per-day-salary' ? getPerDaySalaryColumns() :
                              reportType[0] === 'comparison' ? getComparisonColumns() :
                                reportType[0] === 'ot-impact' ? getOTImpactColumns() :
                                  reportType[0] === 'late-penalty' ? getLatePenaltyColumns() :
                                    reportType[0] === 'staff-login-logout' ? getStaffLoginLogoutColumns() :
                                      reportType[0] === 'salary-register' ? [] :
                                        reportType[0] === 'monthly-summary' ? [] :
                                          getSalesColumns()
      }
      dataSource={data}
      rowKey={(record, index) => record.userId || record.id || index}
      size={reportType[0] === 'attendance' ? 'small' : 'middle'}
      className={`sales-table${reportType[0] === 'attendance' ? ' org-attendance-matrix-table' : ''}`}
      pagination={{
        pageSize: 50,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} records`
      }}
      locale={{
        emptyText: (reportType[0] === 'monthly-attendance' || reportType[0] === 'salary-register' || reportType[0] === 'monthly-summary')
          ? 'Preview not available for this detailed report. Please use "Download Excel" button above.'
          : 'No data available'
      }}
      scroll={{ x: reportType[0] === 'attendance' || reportType[0] === 'punch-report' ? 'max-content' : 1200 }}
      summary={(pageData) => {
        if (reportType[0] !== 'absent-report' || !pageData || pageData.length === 0) return null;
        const totalAbsent = pageData.reduce((acc, curr) => acc + Number(curr.totalAbsentDays || 0), 0);

        return (
          <Table.Summary fixed>
            <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 'bold' }}>
              <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
              <Table.Summary.Cell index={1}>{pageData.length} Employees</Table.Summary.Cell>
              <Table.Summary.Cell index={2}></Table.Summary.Cell>
              <Table.Summary.Cell index={3}></Table.Summary.Cell>
              <Table.Summary.Cell index={4}></Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right"><Tag color="red" style={{ fontWeight: '700', fontSize: 13 }}>{totalAbsent}</Tag></Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        );
      }}
      expandable={
        reportType[0] === 'staff-login-logout'
          ? {
              expandedRowRender: renderLoginLogoutDetail,
              rowExpandable: (record) => record.events && record.events.length > 0,
            }
          : undefined
      }
    />
  );

  const showDept = reportType[0] === 'attendance' || reportType[0] === 'monthly-attendance';
  const showEmployeeSelect = reportScope === 'selected';

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar collapsed={collapsed} />

      <Layout style={{ marginLeft: collapsed ? 80 : 200, height: '100vh', overflow: 'hidden' }}>
        <MainHeader 
          collapsed={collapsed} 
          setCollapsed={setCollapsed} 
          title="Organization Reports" 
        />

        <Content style={{ margin: '24px 16px', padding: 0, background: '#f5f5f5', height: 'calc(100vh - 64px - 48px)', overflow: 'auto' }}>
          <div style={{ paddingBottom: '24px' }}>
            <Card className="sales-content-card" bodyStyle={{ padding: '20px' }} style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]} align="bottom">
                <Col xs={24} sm={12} md={showDept && showEmployeeSelect ? 3 : (showDept || showEmployeeSelect ? 4 : 5)}>
                  <div className="modal-field-label">Report Scope:</div>
                  <Select
                    style={{ width: '100%' }}
                    value={reportScope}
                    onChange={setReportScope}
                    dropdownStyle={{ borderRadius: '8px' }}
                  >
                    <Option value="all">All Employees</Option>
                    <Option value="selected">Selected Employees</Option>
                  </Select>
                </Col>

                {showEmployeeSelect && (
                  <Col xs={24} sm={12} md={showDept ? 4 : 6}>
                    <div className="modal-field-label">Select Employees:</div>
                    <Select
                      mode="multiple"
                      style={{ width: '100%' }}
                      value={selectedEmployees}
                      onChange={setSelectedEmployees}
                      placeholder="Select employees..."
                      showSearch
                      optionFilterProp="label"
                      dropdownStyle={{ borderRadius: '8px' }}
                      options={employees.map((emp) => ({
                        value: emp.id,
                        label: emp.name || emp.profile?.name || emp.staffProfile?.name || `Employee ${emp.id}`,
                      }))}
                    />
                  </Col>
                )}

                {showDept && (
                  <Col xs={24} sm={12} md={showEmployeeSelect ? 4 : 5}>
                    <div className="modal-field-label">Department:</div>
                    <Select
                      style={{ width: '100%' }}
                      value={selectedDepartment}
                      onChange={setSelectedDepartment}
                      placeholder="All Departments"
                      dropdownStyle={{ borderRadius: '8px' }}
                      allowClear
                    >
                      <Option value="">All Departments</Option>
                      {departments.map((dept) => (
                        <Option key={dept.name} value={dept.name}>
                          {dept.name}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                )}

                <Col xs={24} sm={12} md={showDept && showEmployeeSelect ? 5 : (showDept || showEmployeeSelect ? 6 : 7)}>
                  <div className="modal-field-label">Report Type:</div>
                  <Cascader
                    style={{ width: '100%' }}
                    options={reportOptions}
                    value={reportType}
                    onChange={(val) => {
                      if (val && val[0] === 'labour-wages') {
                        navigate('/reports/labour-wages');
                        return;
                      }
                      setReportType(val);
                    }}
                    placeholder="Select Report"
                    expandTrigger="hover"
                    dropdownStyle={{ borderRadius: '8px' }}
                  />
                </Col>

                {reportType[0] === 'attendance' ? (
                  <Col xs={24} sm={24} md={showDept && showEmployeeSelect ? 7 : (showDept || showEmployeeSelect ? 7 : 8)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span className="modal-field-label" style={{ margin: 0, fontWeight: 600 }}>Date Selection:</span>
                      <Radio.Group
                        size="small"
                        value={attendanceDateMode}
                        onChange={(e) => {
                          const m = e.target.value;
                          setAttendanceDateMode(m);
                          if (m === 'range' && (!attendanceRange || !attendanceRange[0])) {
                            setAttendanceRange([dayjs(month).startOf('month'), dayjs(month).startOf('month').date(10)]);
                          }
                        }}
                        buttonStyle="solid"
                      >
                        <Radio.Button value="range">Date Range</Radio.Button>
                        <Radio.Button value="month">Full Month</Radio.Button>
                      </Radio.Group>
                    </div>

                    {attendanceDateMode === 'range' ? (
                      <div>
                        <RangePicker
                          style={{ width: '100%', borderRadius: 6 }}
                          value={attendanceRange && attendanceRange[0] && attendanceRange[1] ? [dayjs(attendanceRange[0]), dayjs(attendanceRange[1])] : null}
                          onChange={(dates) => {
                            if (dates && dates[0] && dates[1]) {
                              setAttendanceRange(dates);
                            }
                          }}
                          format="DD MMM YYYY"
                          allowClear={false}
                        />
                        <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            type={isRangePreset('1-10') ? 'primary' : 'default'}
                            onClick={() => applyRangePreset('1-10')}
                            style={{ fontSize: 11, padding: '0 8px', height: 24, borderRadius: 4 }}
                          >
                            1st - 10th
                          </Button>
                          <Button
                            size="small"
                            type={isRangePreset('11-20') ? 'primary' : 'default'}
                            onClick={() => applyRangePreset('11-20')}
                            style={{ fontSize: 11, padding: '0 8px', height: 24, borderRadius: 4 }}
                          >
                            11th - 20th
                          </Button>
                          <Button
                            size="small"
                            type={isRangePreset('21-end') ? 'primary' : 'default'}
                            onClick={() => applyRangePreset('21-end')}
                            style={{ fontSize: 11, padding: '0 8px', height: 24, borderRadius: 4 }}
                          >
                            21st - End
                          </Button>
                          <Button
                            size="small"
                            type={isRangePreset('full') ? 'primary' : 'default'}
                            onClick={() => applyRangePreset('full')}
                            style={{ fontSize: 11, padding: '0 8px', height: 24, borderRadius: 4 }}
                          >
                            Full Month
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Select
                        style={{ width: '100%' }}
                        value={month}
                        onChange={setMonth}
                        showSearch
                        optionFilterProp="children"
                        dropdownStyle={{ borderRadius: '8px' }}
                      >
                        {months.map(m => (
                          <Option key={m.value} value={m.value}>
                            {m.label}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Col>
                ) : (
                  <Col xs={24} sm={12} md={showDept && showEmployeeSelect ? 3 : (showDept || showEmployeeSelect ? 4 : 5)}>
                    <div className="modal-field-label">Month:</div>
                    <Select
                      style={{ width: '100%' }}
                      value={month}
                      onChange={setMonth}
                      showSearch
                      optionFilterProp="children"
                      dropdownStyle={{ borderRadius: '8px' }}
                    >
                      {months.map(m => (
                        <Option key={m.value} value={m.value}>
                          {m.label}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                )}

                <Col xs={24} sm={12} md={showDept && showEmployeeSelect ? 4 : (showDept || showEmployeeSelect ? 4 : 5)} style={{ textAlign: 'right' }}>
                  <Button
                    type="primary"
                    shape="round"
                    icon={<DownloadOutlined />}
                    loading={downloading}
                    onClick={downloadExcel}
                    style={{ width: '100%', height: '32px' }}
                  >
                    Download Excel
                  </Button>
                </Col>
              </Row>
            </Card>

            <Card className="sales-content-card" bodyStyle={{ padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                  {reportType[0] === 'attendance' ? (
                    attendanceDateMode === 'range' && attendanceRange?.[0] && attendanceRange?.[1] ?
                      `Attendance Report (${dayjs(attendanceRange[0]).format('DD MMM YYYY')} to ${dayjs(attendanceRange[1]).format('DD MMM YYYY')})` :
                      `Monthly Attendance Report - ${moment(month).format('MMMM YYYY')}`
                  ) :
                    reportType[0] === 'absent-report' ? 'Absent Report' :
                    reportType[0] === 'monthly-attendance' ? 'Monthly Detailed Attendance Report (Excel Only)' :
                      reportType[0] === 'leave' ? 'Leave Report' :
                        reportType[0] === 'applied-leave' ? 'Applied Leave Report' :
                          reportType[0] === 'leave-balance' ? 'Leave Balance Report' :
                            reportType[0] === 'punch-report' ? 'Monthly Punch Report' :
                              reportType[0] === 'detailed-attendance' ? 'Detailed Attendance Report' :
                                reportType[0] === 'activities' ? 'Activities Report' :
                                  reportType[0] === 'tickets' ? 'Tickets Report' :
                                    reportType[0] === 'meetings' ? 'Meetings Report' :
                                      reportType[0] === 'salary-register' ? 'Salary Register (Excel Only)' :
                                        reportType[0] === 'monthly-summary' ? `Monthly Summary (${(reportType[1] || 'designation').toUpperCase()} WISE)` :
                                          reportType[0] === 'per-day-salary' ? 'Per Day Salary Average Report' :
                                            reportType[0] === 'staff-login-logout' ? 'Staff Login/Logout Report' :
                                              reportType[0] === 'comparison' ? 'Month-over-Month Comparison Report' :
                                                reportType[0] === 'ot-impact' ? 'Overtime Impact Analysis' :
                                                  reportType[0] === 'late-penalty' ? 'Late Penalty Analysis Report' :
                                                  'Visit Report'} {reportType[0] !== 'attendance' ? `- ${moment(month).format('MMMM YYYY')}` : ''}
                </Title>
              </div>

              {reportType[0] === 'attendance' && data.length > 0 && (
                <div style={{
                  background: '#fafafa',
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                  padding: '10px 16px',
                  marginBottom: 16,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 12
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, color: '#595959' }}>
                      Total Staff: <strong style={{ color: '#262626' }}>{data.length}</strong>
                    </span>
                    <span style={{ fontSize: 13, color: '#595959' }}>
                      Period: <strong style={{ color: '#1677ff' }}>
                        {attendanceDateMode === 'range' && attendanceRange?.[0] && attendanceRange?.[1]
                          ? `${dayjs(attendanceRange[0]).format('DD MMM YYYY')} — ${dayjs(attendanceRange[1]).format('DD MMM YYYY')} (${reportData?.daysInMonth || (reportData?.daysList?.length || 0)} Days)`
                          : `${moment(month).format('MMMM YYYY')} (${reportData?.daysInMonth || 0} Days)`}
                      </strong>
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, color: '#8c8c8c' }}>Legend:</span>
                    <Tag color="green" style={{ margin: 0, fontWeight: 600 }}>P: Present</Tag>
                    <Tag color="red" style={{ margin: 0, fontWeight: 600 }}>A: Absent</Tag>
                    <Tag color="orange" style={{ margin: 0, fontWeight: 600 }}>HD: Half Day</Tag>
                    <Tag color="blue" style={{ margin: 0, fontWeight: 600 }}>L: Leave</Tag>
                    <Tag color="purple" style={{ margin: 0, fontWeight: 600 }}>WO: Weekly Off</Tag>
                    <Tag color="gold" style={{ margin: 0, fontWeight: 600 }}>H: Holiday</Tag>
                  </div>
                </div>
              )}

              <Spin spinning={loading}>
                {renderTable()}
              </Spin>
            </Card>
          </div>
        </Content>
      </Layout>

      <Modal
        title={historyTitle}
        open={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        footer={[<Button key="close" type="primary" shape="round" onClick={() => setHistoryModalVisible(false)}>Close</Button>]}
        className="sales-modal"
        width={600}
      >
        <Table 
          size="small"
          dataSource={historyItems}
          pagination={false}
          className="sales-table"
          columns={[
            { title: 'Date', dataIndex: 'date', key: 'date', render: (d) => moment(d).format('DD MMM YYYY') },
            { 
              title: 'Status', 
              dataIndex: 'status', 
              key: 'status', 
              align: 'center',
              render: (v) => {
                const lower = String(v).toLowerCase();
                const cls = lower === 'present' ? 'sales-status-complete' : lower === 'absent' ? 'sales-status-inactive' : 'sales-status-pending';
                return <span className={`sales-status-tag ${cls}`} style={{ fontSize: '11px' }}>{v}</span>;
              }
            },
            { title: 'Minutes', dataIndex: 'minutes', key: 'minutes', align: 'right', render: (m) => <span style={{ fontWeight: '700', color: '#1677ff' }}>{m} m</span> }
          ]}
        />
      </Modal>

      <Modal
        title={
          selectedStaffAbsent ? (
            <div>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#1677ff' }}>
                {selectedStaffAbsent.staffName}
              </span>
              <Tag color="blue" style={{ marginLeft: 8 }}>{selectedStaffAbsent.department}</Tag>
              <Tag style={{ marginLeft: 4 }}>ID: {selectedStaffAbsent.staffId}</Tag>
            </div>
          ) : 'Absent Breakdown'
        }
        open={absentModalOpen}
        onCancel={() => setAbsentModalOpen(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setAbsentModalOpen(false)}>
            Close Overview
          </Button>
        ]}
        width={550}
        destroyOnClose
      >
        {selectedStaffAbsent && (
          <div>
            <div style={{ background: '#FFF1F0', border: '1px solid #FFA39E', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontWeight: 600, color: '#CF1322', fontSize: 13 }}>Salary Month: </span>
                <span style={{ fontWeight: 700, fontSize: 13 }}>{selectedStaffAbsent.salaryMonth}</span>
              </div>
              <div>
                <Tag color="red" style={{ fontSize: 13, fontWeight: 700, padding: '4px 10px' }}>
                  Total Absent: {selectedStaffAbsent.totalAbsentDays} Days
                </Tag>
              </div>
            </div>

            <Table
              dataSource={selectedStaffAbsent.absentDates || []}
              rowKey="date"
              pagination={{ pageSize: 10, showSizeChanger: true }}
              size="small"
              columns={[
                { title: '#', key: 'idx', width: 50, render: (_, __, idx) => idx + 1 },
                { title: 'Date', dataIndex: 'dateFormatted', key: 'dateFormatted', render: (d) => <span style={{ fontWeight: 600 }}>{d}</span> },
                { title: 'Day of Week', dataIndex: 'dayName', key: 'dayName', render: (day) => <Tag color="purple">{day}</Tag> },
                {
                  title: 'Status',
                  dataIndex: 'statusText',
                  key: 'statusText',
                  render: (statusText, item) => (
                    <Tag color={item.status === 'HALF_DAY' ? 'orange' : 'red'} style={{ fontWeight: 600 }}>
                      {statusText}
                    </Tag>
                  )
                }
              ]}
            />
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default OrgReports;
